import { Injectable } from '@angular/core';
import { TenantResolver } from '../../appServices/tenant/tenant.resolver';
import { StorageService } from '../services/storage.service';
import { createSecurePaymentUuid } from './secure-payment-reference';

export const MYPOS_SPLIT_PAYMENT_IDENTITY_KEY =
  'tapgo_pos_mypos_split_identity_v1';

const MAX_UNRESOLVED_SPLIT_INTENTS = 500;

export type SplitPaymentAbandonProof =
  | 'NOT_STARTED'
  | 'DEFINITIVE_NO_DEBIT';

interface SplitPaymentIntent {
  version: 1;
  tenant: string;
  orderId: string;
  purpose: 'partial' | 'tip';
  amountCents: number;
  identity: string;
  createdAt: number;
}

interface SplitPaymentIdentityState {
  version: 1;
  intents: SplitPaymentIntent[];
}

/**
 * Durable identity for one not-yet-recorded myPOS partial payment.
 *
 * A retry after cancel/decline/reconnect receives the same identity, including
 * after an app restart. Once the backend records the payment, complete()
 * removes it so another equal split of the same order receives a new identity.
 * An unresolved identity never expires automatically: abandoning it requires
 * explicit proof that no debit could have happened.
 * This avoids deriving attribution from a stale UI payment counter or balance.
 */
@Injectable({ providedIn: 'root' })
export class MyPosSplitPaymentIdentityService {
  private gate: Promise<unknown> = Promise.resolve();

  constructor(
    private storage: StorageService,
    private tenantResolver: TenantResolver,
  ) {}

  getOrCreate(
    orderId: string | number,
    amount: unknown,
    purpose: 'partial' | 'tip' = 'partial',
  ): Promise<string> {
    return this.serialize(async () => {
      const tenant = await this.requireTenant();
      const normalizedOrderId = this.requireOrderId(orderId);
      const amountCents = this.requireAmountCents(amount);
      const now = Date.now();
      const state = await this.load();
      const existing = state.intents.find(
        (intent) =>
          intent.tenant === tenant &&
          intent.orderId === normalizedOrderId &&
          intent.purpose === purpose &&
          intent.amountCents === amountCents,
      );
      if (existing) {
        return existing.identity;
      }

      if (state.intents.length >= MAX_UNRESOLVED_SPLIT_INTENTS) {
        throw new Error(
          'Split-payment identity capacity is exhausted by unresolved payments. Reconciliation is required before starting another payment.',
        );
      }
      const identity = `split-${createSecurePaymentUuid()}`;
      state.intents.push({
        version: 1,
        tenant,
        orderId: normalizedOrderId,
        purpose,
        amountCents,
        identity,
        createdAt: now,
      });
      await this.persist(state);
      return identity;
    });
  }

  complete(
    orderId: string | number,
    identity: string,
  ): Promise<void> {
    return this.serialize(async () => {
      const tenant = await this.requireTenant();
      const normalizedOrderId = this.requireOrderId(orderId);
      const state = await this.load();
      const remaining = state.intents.filter(
        (intent) =>
          !(
            intent.tenant === tenant &&
            intent.orderId === normalizedOrderId &&
            intent.identity === identity
          ),
      );
      if (remaining.length === state.intents.length) {
        return;
      }
      await this.persist({ version: 1, intents: remaining });
    });
  }

  /**
   * Removes an unresolved identity only after the caller has established that
   * the terminal was never invoked, or has definitive provider evidence that
   * no debit occurred. This is intentionally separate from complete(), which
   * is used only after the backend ACK.
   */
  abandon(
    orderId: string | number,
    identity: string,
    proof: SplitPaymentAbandonProof,
  ): Promise<void> {
    return this.serialize(async () => {
      if (
        proof !== 'NOT_STARTED' &&
        proof !== 'DEFINITIVE_NO_DEBIT'
      ) {
        throw new Error(
          'A definitive no-debit proof is required to abandon a split-payment identity.',
        );
      }
      const tenant = await this.requireTenant();
      const normalizedOrderId = this.requireOrderId(orderId);
      const normalizedIdentity = String(identity ?? '').trim();
      const state = await this.load();
      const remaining = state.intents.filter(
        (intent) =>
          !(
            intent.tenant === tenant &&
            intent.orderId === normalizedOrderId &&
            intent.identity === normalizedIdentity
          ),
      );
      if (remaining.length === state.intents.length) {
        return;
      }
      await this.persist({ version: 1, intents: remaining });
    });
  }

  private serialize<T>(operation: () => Promise<T>): Promise<T> {
    const result = this.gate.then(operation, operation);
    this.gate = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  }

  private async load(): Promise<SplitPaymentIdentityState> {
    const raw = await this.storage.getStrict(
      MYPOS_SPLIT_PAYMENT_IDENTITY_KEY,
    );
    if (!raw) {
      return { version: 1, intents: [] };
    }

    const parsed = JSON.parse(raw);
    if (parsed?.version === 1 && Array.isArray(parsed?.intents)) {
      // Pre-purpose entries only represented split payments. Preserve them as
      // "partial"; a tip of the same amount will now receive a distinct key.
      parsed.intents = parsed.intents.map((intent: any) => ({
        ...intent,
        purpose: intent?.purpose || 'partial',
      }));
    }
    if (
      parsed?.version !== 1 ||
      !Array.isArray(parsed?.intents) ||
      !parsed.intents.every((intent: any) => this.isValidIntent(intent))
    ) {
      throw new Error('Stored myPOS split-payment identity is invalid.');
    }
    return parsed as SplitPaymentIdentityState;
  }

  private async persist(state: SplitPaymentIdentityState): Promise<void> {
    if (state.intents.length === 0) {
      await this.storage.remove(MYPOS_SPLIT_PAYMENT_IDENTITY_KEY);
      if (
        (await this.storage.getStrict(
          MYPOS_SPLIT_PAYMENT_IDENTITY_KEY,
        )) !== null
      ) {
        throw new Error(
          'Split-payment identity removal verification failed.',
        );
      }
      return;
    }
    const serialized = JSON.stringify(state);
    await this.storage.setObject(MYPOS_SPLIT_PAYMENT_IDENTITY_KEY, state);
    if (
      (await this.storage.getStrict(
        MYPOS_SPLIT_PAYMENT_IDENTITY_KEY,
      )) !== serialized
    ) {
      throw new Error(
        'Split-payment identity write verification failed.',
      );
    }
  }

  private isValidIntent(value: any): value is SplitPaymentIntent {
    return (
      value?.version === 1 &&
      typeof value.tenant === 'string' &&
      value.tenant.length > 0 &&
      typeof value.orderId === 'string' &&
      value.orderId.length > 0 &&
      (value.purpose === 'partial' || value.purpose === 'tip') &&
      Number.isInteger(value.amountCents) &&
      value.amountCents > 0 &&
      typeof value.identity === 'string' &&
      /^split-[0-9a-f-]{36}$/i.test(value.identity) &&
      Number.isFinite(value.createdAt)
    );
  }

  private async requireTenant(): Promise<string> {
    const tenant = (await this.tenantResolver.getTenantName())?.trim();
    if (!tenant) {
      throw new Error(
        'Tenant identity is required before starting a myPOS payment.',
      );
    }
    return tenant;
  }

  private requireOrderId(orderId: string | number): string {
    const normalized = String(orderId ?? '').trim();
    if (!normalized) {
      throw new Error(
        'Order identity is required before starting a split myPOS payment.',
      );
    }
    return normalized;
  }

  private requireAmountCents(amount: unknown): number {
    const parsed = Number(String(amount ?? '').trim().replace(',', '.'));
    const cents = Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
    if (cents <= 0) {
      throw new Error(
        'A positive amount is required before starting a split myPOS payment.',
      );
    }
    return cents;
  }
}
